import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';

const VendorMyBids = ({ onNavigate, userInfo }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendorData, setVendorData] = useState({
    vendor_name: "Loading...",
    vendor_id: "...",
    contact_person: "",
    email: "",
    phone: "",
    company_type: "",
    vendor_status: "active"
  });

  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('submission_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
      if (window.innerWidth > 1024) {
        setIsSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset hover states when sidebar closes
  useEffect(() => {
    if (!isSidebarOpen) {
      setHoveredItem(null);
    }
  }, [isSidebarOpen]);

  // Fetch Vendor Bids
  // Fetch Vendor Bids
  useEffect(() => {
    const fetchVendorBids = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        // Try to get vendor ID from multiple sources
        const vendorId = userInfo?.vendor_id || userInfo?.id || userInfo?.userId || 'VEN2025001';

        console.log('=== VENDOR BIDS FETCH DEBUG ===');
        console.log('UserInfo:', userInfo);
        console.log('Using Vendor ID:', vendorId);
        console.log('Token exists:', !!token);

        if (!token) {
          console.warn('No token found, using demo data');
          setVendorData(prev => ({
            ...prev,
            vendor_name: userInfo?.name || "Vendor User",
            vendor_id: vendorId
          }));
          setLoading(false);
          return;
        }

        const apiUrl = `${API_LINK}/api/vendor/bids?vendorId=${vendorId}`;
        console.log('API URL:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          if (response.status === 401) {
            handleLogout();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('API Response:', result);
        console.log('Bids found:', result.data?.bids?.length || 0);

        if (result.success && result.data) {
          setBids(result.data.bids || []);
          setVendorData({
            vendor_name: userInfo?.name || result.data.vendorId || "Vendor",
            vendor_id: result.data.vendorId || vendorId
          });
        } else {
          throw new Error(result.message || 'Failed to fetch bids');
        }

      } catch (error) {
        console.error('Error fetching bids:', error);
        setError(`Failed to load bids: ${error.message}`);
        setVendorData({
          vendor_name: userInfo?.name || "Vendor User",
          vendor_id: userInfo?.vendor_id || userInfo?.id || userInfo?.userId || 'VEN2025001'
        });
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchVendorBids, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Filter and sort bids
  useEffect(() => {
    let filtered = bids.filter(bid => {
      const matchesSearch = bid.tender_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bid.tender_number?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = filterStatus === 'all' ||
        bid.bid_status?.toLowerCase().replace(/\s+/g, '_') === filterStatus;

      return matchesSearch && matchesFilter;
    });

    // Sort bids
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'submission_date':
          aValue = new Date(a.submission_date);
          bValue = new Date(b.submission_date);
          break;
        case 'total_amount':
          aValue = parseFloat(a.total_amount) || 0;
          bValue = parseFloat(b.total_amount) || 0;
          break;
        case 'tender_title':
          aValue = a.tender_title?.toLowerCase() || '';
          bValue = b.tender_title?.toLowerCase() || '';
          break;
        default:
          aValue = a.submission_date;
          bValue = b.submission_date;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredBids(filtered);
  }, [bids, searchTerm, filterStatus, sortBy, sortOrder]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    if (onNavigate) {
      switch (page) {
        case 'Dashboard':
          onNavigate('vendor-dashboard');
          break;
        case 'Live Bidding':
          onNavigate('live-bidding');
          break;
        case 'My Bids':
          onNavigate('my-bids');
          break;
        case 'Profile':
          onNavigate('vendor-profile');
          break;
        case 'Logout':
          handleLogout();
          break;
        default:
          console.log('Unknown navigation:', page);
      }
    }
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Navigation items
  const navigationItems = [
    { name: 'Dashboard', icon: '📊', badge: null },
    { name: 'Live Bidding', icon: '📦', badge: null },
    { name: 'My Bids', icon: '📄', badge: bids.length },
  ];

  // Download quotation
  const handleDownloadQuotation = async (bidId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const apiUrl = `${API_LINK}/api/vendor/quotation/download/${bidId}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download quotation');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${bidId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error downloading quotation:', error);
      alert('Failed to download quotation');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get user initials
  const getUserInitials = (name) => {
    if (!name) return 'VE';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const statusKey = status?.toLowerCase().replace(/\s+/g, '_') || 'pending';
    const colors = {
      'submitted': { bg: '#e3f2fd', color: '#1976d2' },
      'under_review': { bg: '#fff8e1', color: '#f57c00' },
      'won': { bg: '#e8f5e9', color: '#2e7d32' },
      'lost': { bg: '#ffebee', color: '#c62828' },
      'active': { bg: '#e1f5fe', color: '#0277bd' },
      'pending': { bg: '#fce4ec', color: '#c2185b' }
    };
    return colors[statusKey] || { bg: '#f5f5f5', color: '#666' };
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading your bids...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      backgroundColor: '#f4f7fa',
      color: '#333',
      lineHeight: 1.6,
      minHeight: '100vh',
      width: '100%',
      overflowX: 'hidden'
    }}>
      {/* Global CSS Reset */}
      <style>
        {`
          *, *::before, *::after {
            box-sizing: border-box;
          }

          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            overflow-x: hidden;
          }
        `}
      </style>

      {/* Navigation Bar */}
      <nav style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: isMobile ? '0.75rem 1rem' : '1.5rem 4rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        right: 0,
        left: isMobile ? 0 : '280px',
        width: isMobile ? '100%' : 'calc(100% - 280px)',
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        minHeight: isMobile ? '60px' : '140px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.75rem' : '2rem'
        }}>
          <img
            src="/logo.png"
            alt="Swami Vivekananda University Logo"
            style={{
              width: isMobile ? '40px' : '120px',
              height: isMobile ? '40px' : '120px',
              objectFit: 'contain'
            }}
          />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem'
          }}>
            <h1 style={{
              fontSize: isMobile ? '1rem' : '1.8rem',
              fontWeight: 'bold',
              margin: 0,
              whiteSpace: 'nowrap'
            }}>
              Swami Vivekananda University
            </h1>
            <p style={{
              fontSize: '1rem',
              margin: 0,
              opacity: 0.9,
              borderLeft: '2px solid rgba(255, 255, 255, 0.5)',
              paddingLeft: '1.5rem',
              whiteSpace: 'nowrap',
              display: isMobile ? 'none' : 'block'
            }}>
              My Bids Portal
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: '#ffffff',
            display: isMobile ? 'block' : 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
        >
          {isSidebarOpen ? '✕' : '☰'}
        </button>
      </nav>

      {/* Sidebar */}
      <aside style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? (isSidebarOpen ? 0 : '-280px') : 0,
        width: '280px',
        height: '100%',
        background: '#ffffff',
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
        transition: 'left 0.3s ease',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}>
        {/* Profile Section */}
        <div style={{
          background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
          color: '#ffffff',
          padding: '2.5rem 1.5rem',
          textAlign: 'center',
          borderBottom: '3px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '2.5rem',
            fontWeight: 'bold',
            border: '3px solid rgba(255, 255, 255, 0.4)'
          }}>
            {getUserInitials(vendorData.vendor_name)}
          </div>
          <h3 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1.2rem',
            fontWeight: 'bold'
          }}>
            {vendorData.vendor_name}
          </h3>
          <p style={{
            margin: '0 0 0.25rem 0',
            fontSize: '0.9rem',
            opacity: 0.9
          }}>
            Vendor ID: {vendorData.vendor_id}
          </p>
          <div style={{
            display: 'inline-block',
            padding: '0.35rem 1rem',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '20px',
            fontSize: '0.85rem',
            marginTop: '0.5rem'
          }}>
            ● Active
          </div>
        </div>

        {/* Navigation Menu */}
        <nav style={{
          flex: 1,
          padding: '1.5rem 0'
        }}>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            {navigationItems.map((item, index) => (
              <li key={index} style={{ margin: '0.5rem 0' }}>
                <button
                  onClick={() => handleNavigation(item.name)}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    width: '100%',
                    padding: '1rem 1.5rem',
                    background: hoveredItem === index ? 'rgba(35, 133, 72, 0.1)' : 'transparent',
                    border: 'none',
                    borderLeft: hoveredItem === index ? '4px solid #238548' : '4px solid transparent',
                    color: hoveredItem === index ? '#238548' : '#666',
                    fontSize: '1rem',
                    fontWeight: hoveredItem === index ? 'bold' : 'normal',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.name}</span>
                  {item.badge > 0 && (
                    <span style={{
                      background: '#238548',
                      color: '#ffffff',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div style={{ padding: '1.5rem' }}>
          <button
            onClick={() => handleNavigation('Logout')}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Main Content */}
      <main style={{
        marginLeft: isMobile ? 0 : '280px',
        marginTop: isMobile ? '60px' : '140px',
        padding: isMobile ? '1.5rem' : '2.5rem',
        minHeight: 'calc(100vh - 140px)',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Header */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            margin: '0 0 0.5rem 0',
            fontSize: isMobile ? '1.5rem' : '2rem',
            color: '#238548'
          }}>
            My Bids 📄
          </h2>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '1rem'
          }}>
            Track and manage all your submitted bids. View status, download quotations, and monitor progress.
          </p>
        </section>

        {error && (
          <div style={{
            background: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <p style={{ margin: 0, color: '#c62828' }}>{error}</p>
          </div>
        )}

        {/* Search and Filter */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '1rem',
            alignItems: isMobile ? 'stretch' : 'center'
          }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Search bids by tender title or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="submission_date">Sort by Date</option>
                <option value="total_amount">Sort by Amount</option>
                <option value="tender_title">Sort by Title</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                style={{
                  padding: '0.75rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </section>

        {/* Bids List */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.3rem',
              color: '#333'
            }}>
              Your Bids ({filteredBids.length})
            </h3>
          </div>

          {filteredBids.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              color: '#999'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
              <h4 style={{ margin: '0 0 1rem 0', color: '#666' }}>No bids found</h4>
              <p style={{ margin: 0 }}>
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'You haven\'t submitted any bids yet. Start by browsing available tenders.'}
              </p>
              <button
                onClick={() => handleNavigation('Live Bidding')}
                style={{
                  marginTop: '1.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#238548',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#1e7139'}
                onMouseLeave={(e) => e.target.style.background = '#238548'}
              >
                Browse Tenders
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
              gap: '1.5rem'
            }}>
              {filteredBids.map((bid) => {
                const statusStyle = getStatusColor(bid.bid_status);
                return (
                  <div key={bid.bid_id} style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    border: '1px solid #e9ecef'
                  }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '1.1rem',
                          color: '#238548',
                          fontWeight: 'bold'
                        }}>
                          {bid.tender_title}
                        </h4>
                        <p style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.9rem',
                          color: '#666'
                        }}>
                          Tender #{bid.tender_number}
                        </p>
                      </div>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        background: statusStyle.bg,
                        color: statusStyle.color,
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}>
                        {bid.bid_status}
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                      marginBottom: '1rem',
                      fontSize: '0.9rem'
                    }}>
                      <div>
                        <span style={{ color: '#666' }}>Quoted Amount:</span>
                        <br />
                        <span style={{ fontWeight: 'bold', color: '#238548' }}>
                          {formatCurrency(bid.total_amount)}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#666' }}>Submission Date:</span>
                        <br />
                        <span style={{ fontWeight: 'bold' }}>
                          {formatDate(bid.submission_date)}
                        </span>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.85rem',
                      color: '#666'
                    }}>
                      <span>Tax: {formatCurrency(bid.tax_amount)}</span>
                      <button
                        onClick={() => handleDownloadQuotation(bid.bid_id)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#238548',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#1e7139'}
                        onMouseLeave={(e) => e.target.style.background = '#238548'}
                      >
                        📄 Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default VendorMyBids;
